/**
 * The launcher: a square tile per widget, sized so all of them show with no
 * scrolling (`launcherFit.ts`). An owner widget is not a member's (D23), and
 * a widget the person hid in Settings is left out (`shown.ts`).
 */
import { useEffect, useRef, useState } from "react";

import { ICONS, LAUNCHER_LABELS } from "../widgets/icons";
import { fitTiles, type Fit } from "./launcherFit";
import styles from "./Launcher.module.css";
import { useShownWidgets } from "./shown";
import { useLayout } from "./useLayout";

/** A spacing rung, read from the page, so the arithmetic and the CSS agree. */
function rung(node: HTMLElement, name: string): number {
  return Number.parseFloat(getComputedStyle(node).getPropertyValue(name)) || 0;
}

export function Launcher() {
  const open = useLayout((s) => s.open);
  const box = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<Fit | null>(null);
  const entries = useShownWidgets().filter((entry) => entry.launcher);

  useEffect(() => {
    const node = box.current;
    if (!node) return;
    const measure = () =>
      setFit(
        fitTiles(
          node.clientWidth,
          node.clientHeight,
          entries.length,
          rung(node, "--sp-6"),
          rung(node, "--sp-7"),
        ),
      );
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [entries.length]);

  return (
    <div className={styles.launcher} ref={box}>
      <div
        className={styles.grid}
        style={fit ? { gridTemplateColumns: `repeat(${fit.cols}, ${fit.size}px)` } : undefined}
      >
        {entries.map((entry) => (
          <button key={entry.key} className={styles.tile} onClick={() => open({ name: entry.key })}>
            <span className={styles.icon}>{ICONS[entry.key]}</span>
            <span className={styles.label}>{LAUNCHER_LABELS[entry.key] ?? entry.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
